import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { FiInbox, FiLock, FiSearch } from 'react-icons/fi';
import { Button } from 'components/common';
import useAppSettings from './app-settings/useAppSettings';
import SettingsToolbar from './app-settings/SettingsToolbar';
import SettingsSidebar from './app-settings/SettingsSidebar';
import SettingRow from './app-settings/SettingRow';
import LogoRow from './app-settings/LogoRow';
import UnsavedBar from './app-settings/UnsavedBar';
import { OPLA_SYNC_KEYS, buildGroupStats, groupMeta, humanizeGroup } from './app-settings/constants';

// ── Section headers ───────────────────────────────────────────────────────────

const SectionHeader = ({ groupKey, label, count }) => (
  <div className="tw-sticky tw-top-0 tw-z-10 tw-flex tw-items-center tw-gap-2 tw-px-5 tw-py-2.5 tw-bg-slate-50/95 tw-backdrop-blur tw-border-b tw-border-slate-200">
    <span className="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-shrink-0" style={{ background: groupMeta(groupKey).dot }} />
    <span className="tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-widest tw-text-slate-500">{label}</span>
    <span className="tw-text-[11px] tw-tabular-nums tw-text-slate-400">{count}</span>
  </div>
);
SectionHeader.propTypes = { groupKey: PropTypes.string, label: PropTypes.string.isRequired, count: PropTypes.number.isRequired };

const SubSectionHeader = ({ label, count }) => (
  <div className="tw-flex tw-items-center tw-gap-2.5 tw-px-5 tw-py-2 tw-bg-white tw-border-b tw-border-slate-100">
    <span className="tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-400">{label}</span>
    <span className="tw-h-px tw-flex-1 tw-bg-slate-100" />
    <span className="tw-text-[11px] tw-tabular-nums tw-text-slate-300">{count}</span>
  </div>
);
SubSectionHeader.propTypes = { label: PropTypes.string.isRequired, count: PropTypes.number.isRequired };

// ── Empty states ──────────────────────────────────────────────────────────────

const EmptyState = ({ icon: Icon, title, body, action }) => (
  <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-py-16 tw-px-6 tw-text-center">
    <div className="tw-w-10 tw-h-10 tw-rounded-lg tw-bg-slate-100 tw-flex tw-items-center tw-justify-center">
      <Icon className="tw-text-slate-400" size={18} />
    </div>
    <p className="tw-text-[13px] tw-font-semibold tw-text-slate-700 tw-mb-0">{title}</p>
    <p className="tw-text-[12px] tw-text-slate-500 tw-mb-0 tw-max-w-sm">{body}</p>
    {action}
  </div>
);
EmptyState.propTypes = { icon: PropTypes.elementType.isRequired, title: PropTypes.string.isRequired, body: PropTypes.node, action: PropTypes.node };

// ── Page ──────────────────────────────────────────────────────────────────────

const AppSettings = () => {
  const s = useAppSettings();
  const groupLabel = (key) => s.groups[key] || humanizeGroup(key);
  const stats = useMemo(() => buildGroupStats(s.data), [s.data]);

  // APP_LOGO is not part of the filtered list (it has no text value to search),
  // so it is placed explicitly — and only where it belongs.
  const showLogo = (s.group === 'all' || s.group === 'ui') && !s.subGroup
    && s.filter === 'all' && !s.search.trim();

  const body = () => {
    if (!s.settled && s.settings.length === 0) {
      return (
        <div className="tw-flex tw-flex-col tw-items-center tw-gap-3 tw-py-16">
          <span className="tw-w-6 tw-h-6 tw-border-2 tw-border-primary-500 tw-border-t-transparent tw-rounded-full tw-animate-spin" />
          <p className="tw-text-[13px] tw-text-slate-400 tw-mb-0">Loading settings…</p>
        </div>
      );
    }

    if (s.settings.length === 0) {
      return (
        <EmptyState
          icon={FiInbox}
          title="No settings loaded yet"
          body="Seed from .env to create a row for every key this deployment knows about. Existing values are never overwritten."
          action={<Button size="sm" variant="primary" onClick={s.handleSeed} loading={s.seeding} className="tw-mt-2">Seed from .env</Button>}
        />
      );
    }

    if (s.rows.length === 0) {
      // The logo row is above this and is a row in its own right; an empty state
      // under it would be claiming the group is empty when it is not.
      if (showLogo) return null;

      return (
        <EmptyState
          icon={FiSearch}
          title="Nothing matches"
          body={s.search ? <>No setting matches “<span className="tw-font-medium">{s.search}</span>” here.</> : 'No setting in this group matches the selected filter.'}
          action={(
            <Button
              size="sm"
              variant="secondary"
              className="tw-mt-2"
              onClick={() => { s.setSearch(''); s.setFilter('all'); s.setGroup('all'); }}
            >
              Clear search and filters
            </Button>
          )}
        />
      );
    }

    // HSBC here = the gateway connection; the rest lives elsewhere.
    const hsbcNote = (
      <p key="hsbc-note" role="note" className="tw-m-0 tw-mb-3 tw-text-xs tw-text-geist-fg-3">
        Connection to the gpg-encrypt gateway. The HSBC API credential is set only in the gateway .env. Payer
        details, per-payment defaults and the gateway PGP keys: Payment Request → Settings → HSBC.
      </p>
    );
    const rows = s.rows.flatMap((row) => {
      if (row.type === 'group') {
        return [<SectionHeader key={`g-${row.key}`} groupKey={row.key} label={groupLabel(row.key)} count={row.count} />];
      }
      if (row.type === 'subgroup') {
        const header = <SubSectionHeader key={`s-${row.key}`} label={s.subGroupLabels[row.key] || humanizeGroup(row.key)} count={row.count} />;
        return row.key === 'hsbc' ? [header, hsbcNote] : [header];
      }

      const setting = row.setting;
      const syncable = OPLA_SYNC_KEYS.includes(setting.key);

      return [
        <SettingRow
          key={setting.key}
          setting={setting}
          pendingValue={s.pending[setting.key]}
          onValueChange={s.handleValueChange}
          onRevert={s.handleRevert}
          onReset={s.handleReset}
          showGroupTag={s.group === 'all'}
          onSync={syncable ? () => s.handleSync(setting.key) : undefined}
          syncing={s.syncingKey === setting.key}
        />,
      ];
    });
    return s.subGroup === 'hsbc' ? [hsbcNote, ...rows] : rows;
  };

  return (
    <div className={`tw-mx-auto tw-w-full tw-px-3 sm:tw-px-5 tw-py-4 tw-flex tw-flex-col tw-gap-4 ${s.hasPending ? 'tw-pb-24' : ''}`} style={{ maxWidth: 1440 }}>

      <SettingsToolbar
        search={s.search}
        onSearchChange={s.setSearch}
        searchRef={s.searchRef}
        filter={s.filter}
        onFilterChange={s.setFilter}
        counts={s.counts}
        shown={s.visibleCount}
        total={s.settings.length}
        loading={s.loading}
        seeding={s.seeding}
        hasPending={s.hasPending}
        onRefresh={s.refresh}
        onSeed={s.handleSeed}
      />

      {/* Below lg there is no sidebar, so the group filter lives here instead
          of being unreachable. */}
      <select
        value={s.group}
        onChange={(e) => s.setGroup(e.target.value)}
        aria-label="Settings group"
        className="lg:tw-hidden tw-w-full tw-px-3 tw-py-2 tw-text-[13px] tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-text-slate-700"
      >
        <option value="all">All settings ({stats.total})</option>
        {Object.entries(stats.byGroup).map(([key, bucket]) => (
          <option key={key} value={key}>
            {groupLabel(key)} ({bucket.unset > 0 ? `${bucket.unset} not set` : bucket.total})
          </option>
        ))}
      </select>

      <div className="tw-flex tw-gap-6 tw-items-start">
        <div className="tw-hidden lg:tw-block tw-sticky tw-top-4">
          <SettingsSidebar
            data={s.data}
            groups={s.groups}
            subGroupLabels={s.subGroupLabels}
            activeGroup={s.group}
            activeSubGroup={s.subGroup}
            onSelectGroup={s.setGroup}
            onSelectSubGroup={s.setSubGroup}
          />
        </div>

        <div className="tw-flex-1 tw-min-w-0 tw-flex tw-flex-col tw-gap-3">
          {/* overflow-clip, not hidden: hidden would make a scroll container and
              the sticky section headers would stop sticking. */}
          <div className="tw-bg-white tw-rounded-xl tw-border tw-border-slate-200 tw-overflow-clip">
            {showLogo && (
              <>
                <SectionHeader groupKey="ui" label={groupLabel('ui')} count={1} />
                <LogoRow logoSetting={s.logoSetting} onUpload={s.handleUploadLogo} onDelete={s.handleDeleteLogo} />
              </>
            )}
            {body()}
          </div>

          <div className="tw-flex tw-items-start tw-gap-2.5 tw-px-4 tw-py-3 tw-rounded-lg tw-bg-slate-50 tw-border tw-border-slate-200">
            <FiLock className="tw-text-slate-400 tw-shrink-0 tw-mt-0.5" size={13} />
            <p className="tw-text-[12px] tw-text-slate-500 tw-mb-0 tw-leading-relaxed">
              Keys marked <span className="tw-font-semibold tw-text-slate-600">Encrypted</span> are stored AES-256-GCM
              encrypted at rest. The API never returns their raw value — the browser only ever sees a mask, so typing a
              new value is the only way to change one.
            </p>
          </div>
        </div>
      </div>

      {s.hasPending && (
        <UnsavedBar keys={s.pendingKeys} onSave={s.handleSave} onDiscard={s.handleDiscard} saving={s.saving} />
      )}
    </div>
  );
};

export default AppSettings;
